import React from 'react'
import { RouteObject, useRoutes } from 'react-router-dom'
import { useUser } from '../hooks/useUser'
import { isInternal } from '../utils'
import { roleRouteTree } from './helpers'
import { relativize } from './relativize'

// The brand page set, mounted under /portal/:brandSlug/* (TDC-1609).
//
// It reuses the SAME route trees the brand routers declare, re-rooted by
// relativize(). Mounting the pages once, from one source, is the point: a
// second hand-maintained copy would drift the moment anyone adds a route.
//
// Two things are deliberately discarded from the role tree's root node:
//   - LayoutAdmin — RmnBrandLayout already renders the portal chrome
//     (EdgeShell -> AppShell). Keeping it would nest AppShell inside AppShell.
//   - RouteRoleGuard — replaced at the portal boundary by RmnAuthGuard, which
//     is strictly stronger here: it also 403s a client who types a brand slug
//     they have no membership on, while still letting internal staff through.
// Only `children` survives, which is what relativize() is handed.
const portalRouteObjects = (role: string | null, subscription: any): RouteObject[] => {
  // Internal staff have no brand role of their own; they open a workspace to
  // see what the client sees, so they get the fullest brand tree.
  //
  // The subscription matters as much as the role: generateRoutes indexes the
  // brand roles as roleRoutesMap[role][String(subscription)], and the only key
  // that exists is 'TDC'. An internal user's subscription is null, which
  // stringifies to 'null' and yields an EMPTY route tree — the portal then
  // renders its chrome around nothing, which looks like a blank page rather
  // than an error.
  const staff = isInternal(String(role))
  const effectiveRole = staff ? 'BRAND_ADMIN' : role
  const effectiveSubscription = staff ? 'TDC' : subscription
  const tree = roleRouteTree(effectiveRole as any, effectiveSubscription)
  // The role trees wrap everything in a single `path: '/'` layout route.
  // (brandMemberTDC also carries a stray second top-level catch-all — find()
  // naturally ignores it.)
  const root = tree.find((route) => route.path === '/')
  if (!root || !root.children) return []
  return relativize(root.children)
}

export function PortalRoutes() {
  const { role, subscription } = useUser()
  const routes = React.useMemo(
    () => portalRouteObjects(role as any, subscription),
    [role, subscription],
  )
  if (process.env.NODE_ENV !== 'production' && routes.length === 0) {
    // Empty means the role/subscription pair resolved to no tree; the portal
    // would otherwise render chrome around a blank page.
    // eslint-disable-next-line no-console
    console.warn('PortalRoutes: no routes for role', role, 'subscription', subscription)
  }
  return useRoutes(routes)
}
